import type { HostCapabilities } from "../../capabilities";
import type { MarlinStoreDb } from "../infra/db";
import type { IpJsonContent, SingleSite, SiteMap } from "../types";
import * as machines from "./machines";
import * as ipJson from "./ipJson";
import { warn } from "../infra/effects";
import { assertValidSite } from "./validate";

export const createSitesApi = (
  db: MarlinStoreDb,
  capabilities: Pick<HostCapabilities, "files" | "syncIntegration">,
) => {
  const { files } = capabilities;
  const publish = async (content: IpJsonContent) => {
    await files?.writeIpJson(content).catch(warn("ip.json write"));
    await capabilities
      .syncIntegration?.("site")
      .catch(warn("integration sync"));
  };
  const saveAndMirror = (site: SingleSite) =>
    db.transaction("rw", [db.machines, db.settings], async () => {
      const { sites, previous } = await machines.saveSite(db, site);
      const content = await ipJson.mirrorSiteIntoIpJson(db, site, previous);
      return { sites, content };
    });
  return {
    getMachine: (id: number) => machines.getMachine(db, id),
    getAllSites: () => machines.getAllSites(db),
    getMachines: () => machines.getMachines(db),
    getMachinesLoaded: () => machines.getMachinesLoaded(db),
    removeMachine: (machineId: number) => machines.removeMachine(db, machineId),
    saveSite: async (site: SingleSite): Promise<SiteMap> => {
      const { sites, content } = await saveAndMirror(site);
      await publish(content);
      return sites;
    },
    refreshSites: async (probed: SingleSite[]): Promise<SiteMap> => {
      const content = await db.transaction(
        "rw",
        [db.machines, db.settings],
        async () => {
          let mirrored: IpJsonContent | undefined;
          for (const site of probed) {
            const current = await db.machines.get(site._id);
            if (current?.ip !== site.ip || current.port !== site.port) continue;
            try {
              assertValidSite(site);
            } catch (error) {
              console.warn(`Skipped the probe answer for ${site.ip}`, error);
              continue;
            }
            mirrored = (await saveAndMirror(site)).content;
          }
          return mirrored;
        },
      );
      if (content) await publish(content);
      return machines.getAllSites(db);
    },
    removeSite: async (site: SingleSite): Promise<SiteMap> => {
      const { content, sites } = await db.transaction(
        "rw",
        db.tables,
        async () => ({
          content: await ipJson.removeSiteFromIpJson(db, site),
          sites: await machines.removeSite(db, site),
        }),
      );
      if (content)
        await files?.writeIpJson(content).catch(warn("ip.json write"));
      await capabilities
        .syncIntegration?.("site")
        .catch(warn("integration sync"));
      return sites;
    },
  };
};

export type SitesApi = ReturnType<typeof createSitesApi>;
